import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  useColorModeValue,
  Icon,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FiArrowLeft } from "react-icons/fi";
import { useState, useEffect } from "react";
import { HeroTexture } from "../common/HeroTexture";
import { useDashboardNavigate } from "../../hooks/useDashboardNavigate";

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);
const MotionText = motion(Text);

// Typewriter hook with shouldStart parameter
const useTypewriter = (
  text: string,
  speed: number = 100,
  delay: number = 500,
  shouldStart: boolean = true,
) => {
  const [displayText, setDisplayText] = useState("");

  useEffect(() => {
    if (!shouldStart) return;

    const timeout = setTimeout(() => {
      let i = 0;
      const interval = setInterval(() => {
        if (i < text.length) {
          setDisplayText(text.slice(0, i + 1));
          i++;
        } else {
          clearInterval(interval);
        }
      }, speed);

      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(timeout);
  }, [text, speed, delay, shouldStart]);

  return displayText;
};

export const HeroSection = () => {
  const typewriterTarget =
    "سرمایه گذاری آسان و ۲۴ ساعته\nدر انواع صندوق ها";
  const textColor = useColorModeValue("gray.800", "white");
  const omidexColor = useColorModeValue("brand.500", "white");
  const typewriterGradient = useColorModeValue(
    "linear(to-r, black, black)",
    "linear(to-r, brand.400, brand.500)",
  );
  const subtitleColor = useColorModeValue("gray.600", "gray.300");
  const [firstAnimationComplete, setFirstAnimationComplete] = useState(false);
  const goToDashboard = useDashboardNavigate();

  // Typewriter effect for heading - starts after first animation completes
  const typewriterText = useTypewriter(
    typewriterTarget,
    70,
    10,
    firstAnimationComplete,
  );

  // Radial gradient with multiple positions for light and dark themes
  const bgGradient = useColorModeValue(
    "radial-gradient(circle at 85% 45%, rgba(249, 168, 166, 0.5), transparent 50%), radial-gradient(circle at 15% 85%, rgba(249, 168, 166, 0.5), transparent 50%)",
    "radial-gradient(circle at 85% 15%, rgba(173, 11, 6, 0.15), transparent 50%), radial-gradient(circle at 15% 85%, rgba(173, 11, 6, 0.15), transparent 50%)",
  );

  const bgColor = useColorModeValue("white", "dark.900");

  const handleGetStarted = () => {
    goToDashboard();
  };

  return (
    <Box
      id="hero"
      position="relative"
      minH="100vh"
      display="flex"
      alignItems="center"
      bg={bgColor}
      overflow="hidden"
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: bgGradient,
        zIndex: 0,
      }}
    >
      <HeroTexture />
      <Container maxW="container.xl" position="relative" zIndex={2}>
        <VStack spacing={6} align="center" textAlign="center">
          <Heading
            as="h1"
            fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
            fontWeight="extrabold"
            color={textColor}
            lineHeight="short"
            maxW="900px"
          >
            <MotionText
              as="span"
              display="block"
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              onAnimationComplete={() => setFirstAnimationComplete(true)}
              color={omidexColor}
            >
              OMIDEX
            </MotionText>
            <Text
              as="span"
              display="block"
              bgGradient={typewriterGradient}
              bgClip="text"
              whiteSpace="pre-line"
              textShadow={useColorModeValue(
                "0 0 20px rgba(173, 11, 6, 0.15), 0 0 40px rgba(173, 11, 6, 0.1)",
                "0 0 20px rgba(173, 11, 6, 0.3), 0 0 40px rgba(173, 11, 6, 0.2)",
              )}
            >
              {typewriterText}
              <Text
                as="span"
                opacity={typewriterText.length < typewriterTarget.length ? 1 : 0}
                animation="blink 1s infinite"
                sx={{
                  "@keyframes blink": {
                    "0%, 49%": { opacity: 1 },
                    "50%, 100%": { opacity: 0 },
                  },
                }}
              >
                |
              </Text>
            </Text>
          </Heading>

          <MotionVStack
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 1 }}
          >
            <Text
              fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
              color={subtitleColor}
              maxW="700px"
              lineHeight="tall"
            >
              همین حالا سرمایه گذاری را آغاز کنید
            </Text>

            <HStack spacing={4} pt={4} flexWrap="wrap" justify="center">
              <Button
                size={{ base: "md", md: "lg" }}
                colorScheme="brand"
                rightIcon={
                  <Icon verticalAlign="middle" as={FiArrowLeft as any} />
                }
                onClick={handleGetStarted}
                shadow="lg"
                transition="all 0.3s"
              >
                سرمایه گذاری
              </Button>
              {/* <Button
                size={{ base: "md", md: "lg" }}
                variant="secondary"
                colorScheme="brand"
                onClick={handleLearnMore}
                transition="all 0.3s"
              >
                درباره ما
              </Button> */}
            </HStack>
          </MotionVStack>
        </VStack>
      </Container>

      {/* Scroll Indicator - Mouse Icon */}
      <MotionBox
        position="absolute"
        bottom={"20px"}
        left="50%"
        zIndex={2}
        transform="translateX(-50%)"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.5, ease: "easeOut" }}
      >
        <VStack spacing={3} opacity={0.7}>
          <Box
            position="relative"
            width="28px"
            height="40px"
            border="2px solid"
            borderColor={textColor}
            borderRadius="full"
            display="flex"
            justifyContent="center"
            paddingTop="8px"
          >
            <MotionBox
              width="4px"
              height="8px"
              bg={textColor}
              borderRadius="full"
              animate={{
                y: [0, 8, 0],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </Box>
        </VStack>
      </MotionBox>
    </Box>
  );
};
